import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Train,
  Calendar,
  Radio,
  ExternalLink,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BrandChip } from "@/components/BrandChip";
import { Badge } from "@/components/ui/badge";
import DepotMap from "@/components/DepotMap";
import { PhotoCard } from "@/components/PhotoCard";
import { AdminEditTowerDialog } from "@/components/AdminEditTowerDialog";
import { PhotoUploadDialog } from "@/components/PhotoUploadDialog";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TowerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tower, setTower] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setCurrentUser(r.data))
      .catch(() => setCurrentUser(null));
  }, []);

  const fetchTower = useCallback(async () => {
    try {
      setLoading(true);
      const [tRes, pRes] = await Promise.all([
        axios.get(`${API}/towers/${id}`),
        axios.get(`${API}/towers/${id}/photos`).catch(() => ({ data: [] })),
      ]);
      setTower(tRes.data);
      setPhotos(pRes.data || []);
    } catch (e) {
      console.error("Error fetching tower:", e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTower();
  }, [fetchTower]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tower) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70 mb-4">Tower not found.</p>
          <Button onClick={() => navigate("/")} data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to map
          </Button>
        </div>
      </div>
    );
  }

  const statusColor = {
    active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    preserved: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    demolished: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    abandoned: "bg-red-500/20 text-red-400 border-red-500/30",
  }[tower.status] || "bg-amber-500/20 text-amber-400 border-amber-500/30";

  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <BrandChip size={40} showWordmark to={null} />
          </Link>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-amber-500"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#0d0d0d] to-[#0a0a0a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-2 mb-3">
            <Radio className="w-4 h-4 text-amber-500" />
            <span className="text-amber-500 text-xs font-semibold tracking-widest uppercase">
              Interlocking Tower
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-3" data-testid="tower-name">
            {tower.call_letters && (
              <span className="font-mono text-amber-500 mr-3">{tower.call_letters}</span>
            )}
            {tower.name}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-white/60 text-sm">
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" /> {tower.city}, {tower.state}
            </span>
            {tower.railroad && (
              <span className="flex items-center gap-1.5">
                <Train className="w-4 h-4" /> {tower.railroad}
              </span>
            )}
            {tower.year_built && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> Built {tower.year_built}
              </span>
            )}
            <Badge className={`capitalize ${statusColor}`} data-testid="tower-status">
              {tower.status}
            </Badge>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-2 gap-8">
        {/* Map */}
        <Card className="bg-[#111] border-white/10 overflow-hidden">
          <div className="h-[400px]" data-testid="tower-map">
            <DepotMap
              depots={[]}
              towers={[tower]}
              onTowerClick={() => {}}
              loading={false}
              darkMode
              showDepots={false}
              showTowers={true}
              center={[tower.latitude, tower.longitude]}
              zoom={13}
            />
          </div>
        </Card>

        {/* Details */}
        <Card className="bg-[#111] border-white/10">
          <CardContent className="p-6 space-y-5">
            <div>
              <h2 className="font-serif text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" /> Details
              </h2>
              <dl className="space-y-3 text-sm">
                {tower.call_letters && (
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-white/50">Call letters</dt>
                    <dd className="text-white font-mono">{tower.call_letters}</dd>
                  </div>
                )}
                {tower.tower_type && (
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-white/50">Machine type</dt>
                    <dd className="text-white capitalize">{tower.tower_type}</dd>
                  </div>
                )}
                {tower.railroad && (
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-white/50">Railroad</dt>
                    <dd className="text-white">{tower.railroad}</dd>
                  </div>
                )}
                {tower.year_built && (
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-white/50">Year built</dt>
                    <dd className="text-white font-mono">{tower.year_built}</dd>
                  </div>
                )}
                {tower.year_closed && (
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-white/50">Year closed</dt>
                    <dd className="text-white font-mono">{tower.year_closed}</dd>
                  </div>
                )}
                <div className="flex justify-between border-b border-white/5 pb-2">
                  <dt className="text-white/50">Coordinates</dt>
                  <dd className="text-white font-mono text-xs">
                    {tower.latitude.toFixed(4)}, {tower.longitude.toFixed(4)}
                  </dd>
                </div>
              </dl>
            </div>

            {tower.description && (
              <div>
                <h3 className="font-semibold text-white mb-2 text-sm uppercase tracking-wider">
                  Description
                </h3>
                <p className="text-white/70 text-sm leading-relaxed" data-testid="tower-description">
                  {tower.description}
                </p>
              </div>
            )}

            {tower.wikipedia_url && (
              <a
                href={tower.wikipedia_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 text-sm font-medium"
                data-testid="wikipedia-link"
              >
                <ExternalLink className="w-4 h-4" /> Read on Wikipedia
              </a>
            )}

            <div className="pt-3 flex flex-wrap gap-2">
              <a
                href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${tower.latitude},${tower.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/[0.06] border border-white/20 text-white hover:bg-white/[0.14] hover:border-white/30 transition-colors"
                data-testid="tower-street-view-btn"
              >
                <ExternalLink className="w-3 h-3" /> Google Street View
              </a>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${tower.latitude},${tower.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-amber-500 text-black font-semibold hover:bg-amber-600"
                data-testid="tower-directions-btn"
              >
                <MapPin className="w-3 h-3" /> Get Directions
              </a>
            </div>

            {(currentUser?.is_admin || currentUser?.is_mod) && (
              <div className="pt-3 border-t border-white/5">
                <AdminEditTowerDialog
                  tower={tower}
                  onChange={(updated) => setTower(updated)}
                />
              </div>
            )}

            {tower.updated_at && (
              <p
                className="text-[11px] text-white/50 font-mono pt-2"
                data-testid="tower-updated-chip"
                title={`Last edited by ${tower.updated_by || "unknown"}`}
              >
                Updated {new Date(tower.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Photos */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-serif text-2xl font-bold text-white">Photos</h2>
          <PhotoUploadDialog
            subjectId={tower.id}
            subjectKind="tower"
            subjectName={tower.name}
            currentUser={currentUser}
            onUploaded={async () => {
              const pRes = await axios.get(`${API}/towers/${tower.id}/photos`);
              setPhotos(pRes.data || []);
            }}
          />
        </div>
        {photos.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((p) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  subjectId={tower.id}
                  subjectKind="tower"
                  canDelete={!!(currentUser?.is_admin || currentUser?.is_mod)}
                  onDelete={(id) => setPhotos((prev) => prev.filter((x) => x.id !== id))}
                />
              ))}
            </div>
            <p className="text-xs text-white/50 mt-4 max-w-2xl">
              Photos shown here come from preserved railroad archives, public-domain sources, and user submissions.
              If you own the copyright to any image and want it removed or recredited, click the <span className="text-amber-500 font-semibold">Report</span> button
              that appears when you hover over a photo, or email{" "}
              <a href="mailto:takedown@railstream.net" className="text-amber-500 hover:underline">
                takedown@railstream.net
              </a>.
            </p>
          </>
        ) : (
          <p className="text-sm text-white/60">No photos yet. Be the first to add one.</p>
        )}
      </section>
    </div>
  );
}
